import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const IntegrateurInformatique = () => {
  return (
    <>
      <Helmet>
        <title>Intégr

ateur Informatique - ZiExter | Support IT & Intégration</title>
        <meta name="description" content="Des solutions informatiques clés en main, conçues pour votre croissance. Intégration de logiciels, infrastructures complètes, systèmes robustes et évolutifs." />
        <link rel="canonical" href="https://ziexter.com/services/integrateur-informatique" />
      </Helmet>

      {/* Hero */}
      <section className="page-hero bg-gradient-to-br from-secondary to-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl animate-fade-in-up">
            <nav className="flex items-center space-x-2 text-sm mb-4 text-gray-200">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <span>/</span>
              <Link to="/services" className="hover:text-white">Services</Link>
              <span>/</span>
              <span className="text-white">Intégrateur Informatique</span>
            </nav>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Intégrateur Informatique – ZiExter
            </h1>
            <p className="text-xl text-gray-100">
              Des solutions informatiques clés en main, conçues pour votre croissance
            </p>
            <p className="text-lg text-gray-200 mt-4">
              De l'intégration de logiciels à la mise en place d'infrastructures complètes, ZiExter vous accompagne pour construire des systèmes robustes, évolutifs et parfaitement adaptés à vos besoins.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Hero */}
      <section className="py-8 bg-accent">
        <div className="container-custom">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/devis" className="btn btn-secondary">
              Demandez votre audit gratuit
            </Link>
            <Link to="/contact" className="btn btn-outline-white">
              Parlez à un expert IT
            </Link>
          </div>
        </div>
      </section>

      {/* Chiffres clés */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos résultats en intégration informatique</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center hover-lift animate-fade-in-up">
              <div className="text-4xl mb-2">⚙️</div>
              <div className="text-3xl font-bold text-primary mb-2">95%</div>
              <p className="text-sm text-gray-600">de projets déployés dans les délais prévus</p>
            </div>
            <div className="card text-center hover-lift animate-fade-in-up animation-delay-50">
              <div className="text-4xl mb-2">🔒</div>
              <div className="text-3xl font-bold text-primary mb-2">100%</div>
              <p className="text-sm text-gray-600">de conformité aux normes de sécurité (ISO, GDPR)</p>
            </div>
            <div className="card text-center hover-lift animate-fade-in-up animation-delay-100">
              <div className="text-4xl mb-2">🌐</div>
              <div className="text-3xl font-bold text-primary mb-2">+10</div>
              <p className="text-sm text-gray-600">secteurs d'activité couverts</p>
            </div>
            <div className="card text-center hover-lift animate-fade-in-up animation-delay-150">
              <div className="text-4xl mb-2">🚀</div>
              <div className="text-3xl font-bold text-primary mb-2">-30%</div>
              <p className="text-sm text-gray-600">Réduction moyenne des coûts d'infrastructure pour nos clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* Le problème */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto animate-fade-in-up">
            <h2 className="section-title text-center mb-8">Des systèmes complexes ? Des outils mal intégrés ?</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg mb-4">
                De nombreuses entreprises jonglent avec des outils déconnectés et des infrastructures vieillissantes qui freinent leur productivité. Résultat : perte de temps, données dispersées, sécurité fragile.
              </p>
              <p className="text-lg font-semibold text-primary">
                Avec ZiExter, bénéficiez d'un partenaire technique fiable qui intègre, optimise et sécurise vos systèmes pour en faire un moteur de performance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Études de cas */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Nos intégrations font la différence</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <h3 className="font-bold text-lg mb-3">Cas client 1 – Start-up fintech, Paris</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Problème :</strong> Des outils de CRM et de facturation qui ne communiquaient pas entre eux.</li>
                <li><strong>Solution ZiExter :</strong> Mise en place d'une intégration API et migration vers un ERP unifié.</li>
                <li><strong>Résultat :</strong> 40% de gain de temps administratif et zéro erreur de facturation.</li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <h3 className="font-bold text-lg mb-3">Cas client 2 – Groupe de formation, Tunis</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Problème :</strong> Suivi inefficace des apprenants et données dispersées.</li>
                <li><strong>Solution ZiExter :</strong> Déploiement d'une plateforme LMS connectée aux systèmes internes.</li>
                <li><strong>Résultat :</strong> +25% d'engagement des apprenants.</li>
              </ul>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <h3 className="font-bold text-lg mb-3">Cas client 3 – PME industrielle, Lyon</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li><strong>Problème :</strong> Systèmes obsolètes et coûts élevés de maintenance.</li>
                <li><strong>Solution ZiExter :</strong> Externalisation de la gestion IT et modernisation des infrastructures.</li>
                <li><strong>Résultat :</strong> Réduction de 30% des coûts IT et continuité garantie.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Nos chiffres en intégration IT */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">La technologie au service de votre performance</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: '💻', stat: '150', label: 'projets IT & Excel et VBA réussis' },
              { icon: '🕒', stat: '4 sem', label: 'Délai moyen de déploiement' },
              { icon: '🌍', stat: '6', label: 'pays de présence' },
              { icon: '⭐', stat: '96%', label: 'Taux de satisfaction client' }
            ].map((item, i) => (
              <div key={i} className="card text-center hover-lift animate-fade-in-up" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="text-3xl mb-2">{item.icon}</div>
                <div className="text-2xl font-bold text-primary mb-1">{item.stat}</div>
                <p className="text-sm text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avis Clients */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">Avis Clients</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="card hover-lift animate-fade-in-up">
              <p className="text-gray-700 mb-4 italic">
                "Grâce à ZiExter, nos outils sont enfin connectés entre eux. Un vrai gain de productivité !"
              </p>
              <div className="font-semibold">Naomie Laine</div>
              <div className="text-sm text-gray-600">COO Fintech, Paris</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-100">
              <p className="text-gray-700 mb-4 italic">
                "La modernisation de notre système IT nous a permis de réduire nos coûts tout en améliorant la sécurité."
              </p>
              <div className="font-semibold">Antoine Briere</div>
              <div className="text-sm text-gray-600">DG PME industrielle, Nice</div>
            </div>

            <div className="card hover-lift animate-fade-in-up animation-delay-200">
              <p className="text-gray-700 mb-4 italic">
                "Des experts réactifs qui comprennent nos besoins métiers. ZiExter est un partenaire précieux."
              </p>
              <div className="font-semibold">Nadia Maillard</div>
              <div className="text-sm text-gray-600">Responsable formation, Maroc</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-12 animate-fade-in-up">
            <h2 className="section-title">FAQ</h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {[
              { q: 'Quels types de systèmes informatiques intégrez-vous ?', a: 'CRM, ERP, outils collaboratifs, plateformes e-learning, solutions de facturation, etc.' },
              { q: 'Proposez-vous des solutions sur-mesure ?', a: 'Oui, chaque projet est adapté à vos besoins spécifiques et à vos contraintes.' },
              { q: 'Comment garantissez-vous la sécurité de mes données ?', a: 'Nous respectons les standards internationaux de cybersécurité et de conformité (GDPR, ISO).' },
              { q: 'Offrez-vous un support après l\'intégration ?', a: 'Oui, nous assurons la maintenance, le monitoring et l\'assistance continue de vos systèmes.' },
              { q: 'Est-ce que je peux externaliser totalement mon service IT ?', a: 'Absolument, nous proposons des solutions complètes de gestion et maintenance informatique.' }
            ].map((item, i) => (
              <details key={i} className="card cursor-pointer">
                <summary className="font-semibold p-4 flex justify-between items-center">
                  <span>Q : {item.q}</span>
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="p-4 pt-0 text-gray-600">{item.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-gradient-to-r from-primary to-secondary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center animate-fade-in-up">
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
              Un système informatique à la hauteur de vos ambitions
            </h2>
            <p className="text-xl text-gray-100 mb-8">
              Ne laissez pas vos outils limiter votre croissance. Avec ZiExter, intégrez et optimisez vos systèmes pour booster votre performance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/devis" className="btn btn-accent btn-lg">
                Demandez un devis IT
              </Link>
              <Link to="/rendez-vous" className="btn btn-outline-white btn-lg">
                Planifiez un rendez-vous avec un expert
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default IntegrateurInformatique;
